import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type {
  AdMatch,
  CreateTournamentBody,
  DecklistResult,
  JoinTournamentBody,
  LiveState,
  MatchState,
  MyTournaments,
  PlayDecklist,
  PatchTournamentBody,
  PlayNotificationPrefs,
  RoundState,
  TournamentState,
  UsernameAvailability,
} from 'types/interfaces/play';

/**
 * TradeMagic Play API (Asana 1220000146037011). Paths follow the backend plan's
 * Contract delta: everything under /play except the two account endpoints.
 * Public endpoints set requiresAuth: false so a logged-out 401 never logs the
 * visitor out (baseQuery.ts). Mutations invalidate the tournament tag; the
 * socket hooks patch the same cache between refetches.
 */

const PLAY = '/play';

/** Responses may arrive bare or as { success, data }; accept both. */
const unwrap = <T,>(response: unknown): T => {
  if (response && typeof response === 'object' && 'data' in response && 'success' in response) {
    return (response as { data: T }).data;
  }
  return response as T;
};

export const TOURNAMENT_TAG = 'PlayTournament' as const;

export const playApi = createApi({
  reducerPath: 'playApi',
  baseQuery,
  tagTypes: [TOURNAMENT_TAG, 'PlayLive', 'PlayMine', 'PlayNotifications'],
  endpoints: (builder) => ({
    // ---- Tournaments and players (section A)
    getTournament: builder.query<TournamentState, string>({
      query: (code) => ({ url: `${PLAY}/tournaments/${encodeURIComponent(code)}`, method: 'GET', requiresAuth: false }),
      transformResponse: unwrap<TournamentState>,
      providesTags: (_r, _e, code) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    getMyTournaments: builder.query<MyTournaments, void>({
      query: () => ({ url: `${PLAY}/tournaments/mine`, method: 'GET', requiresAuth: true }),
      transformResponse: unwrap<MyTournaments>,
      providesTags: ['PlayMine'],
    }),
    createTournament: builder.mutation<TournamentState, CreateTournamentBody>({
      query: (body) => ({ url: `${PLAY}/tournaments`, method: 'POST', body, requiresAuth: true }),
      transformResponse: unwrap<TournamentState>,
      invalidatesTags: ['PlayMine'],
    }),
    patchTournament: builder.mutation<TournamentState, { id: string; code: string; body: PatchTournamentBody }>({
      query: ({ id, body }) => ({ url: `${PLAY}/tournaments/${id}`, method: 'PATCH', body, requiresAuth: true }),
      transformResponse: unwrap<TournamentState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    joinTournament: builder.mutation<TournamentState, { code: string; body: JoinTournamentBody }>({
      query: ({ code, body }) => ({ url: `${PLAY}/tournaments/${encodeURIComponent(code)}/join`, method: 'POST', body, requiresAuth: true }),
      transformResponse: unwrap<TournamentState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }, 'PlayMine'],
    }),
    addWalkIn: builder.mutation<TournamentState, { id: string; code: string; displayName: string }>({
      query: ({ id, displayName }) => ({ url: `${PLAY}/tournaments/${id}/players`, method: 'POST', body: { displayName }, requiresAuth: true }),
      transformResponse: unwrap<TournamentState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    /** TO drop/reinstate by player id; self-drop passes playerId 'me'. */
    setPlayerStatus: builder.mutation<TournamentState, { id: string; code: string; playerId: string; action: 'DROP' | 'REINSTATE' }>({
      query: ({ id, playerId, action }) => ({ url: `${PLAY}/tournaments/${id}/players/${playerId}`, method: 'PATCH', body: { action }, requiresAuth: true }),
      transformResponse: unwrap<TournamentState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    finishTournament: builder.mutation<TournamentState, { id: string; code: string }>({
      query: ({ id }) => ({ url: `${PLAY}/tournaments/${id}/finish`, method: 'POST', requiresAuth: true }),
      transformResponse: unwrap<TournamentState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }, 'PlayMine'],
    }),
    putDecklist: builder.mutation<DecklistResult, { id: string; code: string; rawText: string; sourceUrl?: string; name?: string; tournamentPlayerId?: string }>({
      query: ({ id, rawText, sourceUrl, name, tournamentPlayerId }) => ({
        url: `${PLAY}/tournaments/${id}/decklist`,
        method: 'PUT',
        body: { rawText, sourceUrl, name, tournamentPlayerId },
        requiresAuth: true,
      }),
      transformResponse: unwrap<DecklistResult>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }, { type: TOURNAMENT_TAG, id: `${code}:deck` }],
    }),

    // ---- Rounds and matches (section B)
    pairNextRound: builder.mutation<RoundState, { id: string; code: string }>({
      query: ({ id }) => ({ url: `${PLAY}/tournaments/${id}/rounds`, method: 'POST', requiresAuth: true }),
      transformResponse: unwrap<RoundState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    repairRound: builder.mutation<RoundState, { roundId: string; code: string }>({
      query: ({ roundId }) => ({ url: `${PLAY}/rounds/${roundId}/repair`, method: 'POST', requiresAuth: true }),
      transformResponse: unwrap<RoundState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    swapPlayers: builder.mutation<RoundState, { roundId: string; code: string; playerIdA: string; playerIdB: string }>({
      query: ({ roundId, playerIdA, playerIdB }) => ({ url: `${PLAY}/rounds/${roundId}/swap`, method: 'POST', body: { playerIdA, playerIdB }, requiresAuth: true }),
      transformResponse: unwrap<RoundState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    roundControl: builder.mutation<RoundState, { roundId: string; code: string; action: 'start' | 'pause' | 'resume' | 'extend' }>({
      query: ({ roundId, action }) => ({ url: `${PLAY}/rounds/${roundId}/${action}`, method: 'POST', requiresAuth: true }),
      transformResponse: unwrap<RoundState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    reportResult: builder.mutation<MatchState, { matchId: string; code: string; gamesMine: number; gamesOpp: number }>({
      query: ({ matchId, gamesMine, gamesOpp }) => ({ url: `${PLAY}/matches/${matchId}/report`, method: 'POST', body: { gamesMine, gamesOpp }, requiresAuth: true }),
      transformResponse: unwrap<MatchState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    setMatchResult: builder.mutation<MatchState, { matchId: string; code: string; gamesA: number; gamesB: number }>({
      query: ({ matchId, gamesA, gamesB }) => ({ url: `${PLAY}/matches/${matchId}/result`, method: 'PUT', body: { gamesA, gamesB }, requiresAuth: true }),
      transformResponse: unwrap<MatchState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    callJudge: builder.mutation<MatchState, { matchId: string; code: string }>({
      query: ({ matchId }) => ({ url: `${PLAY}/matches/${matchId}/judge`, method: 'POST', requiresAuth: true }),
      transformResponse: unwrap<MatchState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    resolveJudgeCall: builder.mutation<MatchState, { judgeCallId: string; code: string }>({
      query: ({ judgeCallId }) => ({ url: `${PLAY}/judge-calls/${judgeCallId}/resolve`, method: 'POST', requiresAuth: true }),
      transformResponse: unwrap<MatchState>,
      invalidatesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: code }],
    }),
    getLive: builder.query<LiveState, string>({
      query: (code) => ({ url: `${PLAY}/tournaments/${encodeURIComponent(code)}/live`, method: 'GET', requiresAuth: false }),
      transformResponse: unwrap<LiveState>,
      providesTags: (_r, _e, code) => [{ type: 'PlayLive', id: code }],
    }),
    getAdMatch: builder.query<AdMatch | null, string>({
      query: (code) => ({ url: `${PLAY}/tournaments/${encodeURIComponent(code)}/ad-match`, method: 'GET', requiresAuth: true }),
      transformResponse: (response: unknown) => {
        const body = unwrap<{ match?: AdMatch | null } | null>(response);
        return body?.match ?? null;
      },
    }),
    /** Own decklist; `{ decklist: null }` when none has been submitted. */
    getMyDecklist: builder.query<PlayDecklist | null, { id: string; code: string }>({
      query: ({ id }) => ({ url: `${PLAY}/tournaments/${id}/decklist`, method: 'GET', requiresAuth: true }),
      transformResponse: (response: unknown) => {
        const body = unwrap<(PlayDecklist & { decklist?: null }) | { decklist: null } | null>(response);
        if (!body || ('decklist' in body && body.decklist === null)) return null;
        return body as PlayDecklist;
      },
      providesTags: (_r, _e, { code }) => [{ type: TOURNAMENT_TAG, id: `${code}:deck` }],
    }),
    getPlayerDecklist: builder.query<PlayDecklist | null, { id: string; playerId: string }>({
      query: ({ id, playerId }) => ({ url: `${PLAY}/tournaments/${id}/decklist/${playerId}`, method: 'GET', requiresAuth: true }),
      transformResponse: (response: unknown) => {
        const body = unwrap<(PlayDecklist & { decklist?: null }) | { decklist: null } | null>(response);
        if (!body || ('decklist' in body && body.decklist === null)) return null;
        return body as PlayDecklist;
      },
    }),

    // ---- Account (section D)
    getUsernameAvailability: builder.query<UsernameAvailability, string>({
      query: (username) => ({ url: `/users/username-available?username=${encodeURIComponent(username)}`, method: 'GET', requiresAuth: false }),
      transformResponse: unwrap<UsernameAvailability>,
    }),
    getPlayNotifications: builder.query<PlayNotificationPrefs, void>({
      query: () => ({ url: `${PLAY}/notifications`, method: 'GET', requiresAuth: true }),
      transformResponse: unwrap<PlayNotificationPrefs>,
      providesTags: ['PlayNotifications'],
    }),
    putPlayNotifications: builder.mutation<PlayNotificationPrefs, Partial<Pick<PlayNotificationPrefs, 'discordDm' | 'sms' | 'push'>>>({
      query: (body) => ({ url: `${PLAY}/notifications`, method: 'PUT', body, requiresAuth: true }),
      transformResponse: unwrap<PlayNotificationPrefs>,
      invalidatesTags: ['PlayNotifications'],
    }),
  }),
});

export const {
  useGetTournamentQuery,
  useLazyGetTournamentQuery,
  useGetMyTournamentsQuery,
  useCreateTournamentMutation,
  usePatchTournamentMutation,
  useJoinTournamentMutation,
  useAddWalkInMutation,
  useSetPlayerStatusMutation,
  useFinishTournamentMutation,
  usePutDecklistMutation,
  usePairNextRoundMutation,
  useRepairRoundMutation,
  useSwapPlayersMutation,
  useRoundControlMutation,
  useReportResultMutation,
  useSetMatchResultMutation,
  useCallJudgeMutation,
  useResolveJudgeCallMutation,
  useGetLiveQuery,
  useGetAdMatchQuery,
  useGetMyDecklistQuery,
  useGetPlayerDecklistQuery,
  useLazyGetUsernameAvailabilityQuery,
  useGetPlayNotificationsQuery,
  usePutPlayNotificationsMutation,
} = playApi;
